/**
 * WP-07A — Server-generated object keys.
 *
 * Every rule here exists because the alternative is a real attack:
 *   * The path is DERIVED from ids the server already holds, so a caller cannot
 *     choose where bytes land, cannot target another workspace's prefix, and
 *     cannot traverse out of its own prefix.
 *   * No file name and no extension ever appear in the path, so a user-chosen
 *     name can neither leak through a URL nor influence content sniffing.
 *   * The suffix is the fixed literal `payload`, so one evidence record owns
 *     exactly one object and an upsert has nowhere to go.
 *
 * The identical shape is pinned by a CHECK constraint in the proposed migration,
 * so a path that disagrees with this function cannot be stored at all.
 */

export const EVIDENCE_BUCKET = "loop-evidence" as const;

/** 10 MiB, the governed MVP ceiling, expressed in bytes exactly once. */
export const MAX_EVIDENCE_BYTES = 10_485_760;

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export interface EvidenceObjectCoordinates {
  readonly workspaceId: string;
  readonly loopId: string;
  readonly evidenceId: string;
}

export function isUuid(value: unknown): value is string {
  return typeof value === "string" && UUID_PATTERN.test(value);
}

/** Throws rather than returning a partially-formed path: fail closed. */
export function evidenceObjectPath(coordinates: EvidenceObjectCoordinates): string {
  const { workspaceId, loopId, evidenceId } = coordinates;
  if (!isUuid(workspaceId) || !isUuid(loopId) || !isUuid(evidenceId)) {
    throw new Error("evidence_object_path_requires_uuids");
  }
  return [
    "workspaces",
    workspaceId.toLowerCase(),
    "loops",
    loopId.toLowerCase(),
    "evidence",
    evidenceId.toLowerCase(),
    "payload",
  ].join("/");
}

/**
 * Ownership validation: does this object path belong to exactly the expected
 * workspace, Loop and evidence record? Used before any read authorization, so a
 * stored path that was somehow tampered with cannot be served.
 */
export function objectPathMatches(path: string, coordinates: EvidenceObjectCoordinates): boolean {
  try {
    return path === evidenceObjectPath(coordinates);
  } catch {
    return false;
  }
}

/** Structural sanity for a path read back from storage or the database. */
export function isWellFormedEvidencePath(path: unknown): path is string {
  if (typeof path !== "string") return false;
  if (path.includes("..") || path.includes("//") || path.startsWith("/")) return false;
  const parts = path.split("/");
  if (parts.length !== 7) return false;
  return (
    parts[0] === "workspaces" &&
    parts[2] === "loops" &&
    parts[4] === "evidence" &&
    parts[6] === "payload" &&
    isUuid(parts[1]) &&
    isUuid(parts[3]) &&
    isUuid(parts[5])
  );
}
